import axios from 'axios'
import {
  CSRF_HEADER,
  captureCsrfToken,
  getCsrfToken,
  refreshCsrfToken,
} from './csrfToken'

/**
 * Attach the Rails CSRF token to state-changing requests on an axios instance.
 *
 * PLT-2324 — the legacy frontend sends no token on any XHR, including login,
 * which makes it the largest tokenless write surface in the product and the
 * blocker on PLT-2105's `CSRF_MODE=enforce` flip.
 *
 * WHY THIS IS A FUNCTION TAKING AN INSTANCE, AND NOT ONE REGISTRATION. There is
 * no single axios object in this app. `AxiosService.js` builds one with
 * `axios.create()`, `api/index.js` builds another per `getApi()` call, and ~31
 * modules import the bare default export. **An axios instance inherits config
 * from the default but NOT its interceptors**, so registering once anywhere
 * covers exactly one of them. Each instance that issues cookie-authenticated
 * writes has to opt in — which is also the trap for the next one: an
 * `axios.create()` added later reaches Rails with no token and nothing fails
 * loudly, it just goes out unverified.
 *
 * TWO GATES, and the origin one is the control that matters. A CSRF token is
 * session-scoped and useless elsewhere, but handing one to a third-party host is
 * still a leak, so a request is only stamped when its resolved origin is the
 * page's own. The method gate keeps it off reads, which Rails never verifies.
 * Both fail closed: an unparseable URL is treated as external.
 *
 * The token is read per request rather than captured once, because Devise
 * rotates the session — and with it the token — on sign-in. With no
 * `csrf_meta_tags` on the page the read returns null, nothing is attached, and
 * the request behaves exactly as it does today.
 */
const WRITE_METHODS = ['post', 'put', 'patch', 'delete']

const isSameOriginRequest = (url, baseURL) => {
  try {
    // A relative URL resolves against `baseURL` when the instance sets one, and
    // otherwise against the page — which is the Rails origin on these pages.
    const base = baseURL
      ? new URL(baseURL, window.location.href).toString()
      : window.location.href

    return new URL(url ?? '', base).origin === window.location.origin
  } catch (error) {
    return false
  }
}

const isSameOriginWrite = (config) =>
  Boolean(config) &&
  WRITE_METHODS.includes((config.method || '').toLowerCase()) &&
  isSameOriginRequest(config.url, config.baseURL)

/**
 * PLT-2867 — re-issue a write refused for a stale token, once.
 *
 * WHY A REFRESH COMES FIRST. Under `:null_session` the refused write ran with a
 * `NullCookieJar`, so the remember cookie could not recreate the session and the
 * 401 carries no new token. Re-sending straight away would send the same stale
 * token and fail again. `refreshCsrfToken` runs a GET with the full cookie jar,
 * which recreates the session and returns its token.
 *
 * WHY IT IS SAFE. The refused write applied nothing: `:null_session` ran the action
 * with no user, and Devise answered 401 before any change. So sending it again
 * cannot apply it twice.
 *
 * BOUNDED, in every direction:
 *   - same-origin writes only, and only on a 401 that came with a response; a
 *     request that got no response (offline, aborted) is never retried;
 *   - only when the refresh says a user is signed in, so a logged-out or
 *     force-logged-out user gets the original 401, not a second one;
 *   - only when the token changed, so a 401 for any other reason is not retried;
 *   - at most once: the retried config carries `__csrfRetried`, and a second 401
 *     reaches the caller's own error handling unchanged.
 *
 * Axios only. jQuery has no clean way to re-issue a request, so `jquery_ujs` and
 * `$.ajax` writes rely on the proactive refresh in `installCsrfLifecycleRefresh`.
 */
const RETRIED = '__csrfRetried'

const sentToken = (config) => {
  const headers = config?.headers || {}
  const name = Object.keys(headers).find(
    (key) => key.toLowerCase() === CSRF_HEADER.toLowerCase()
  )

  return name ? headers[name] : null
}

const shouldRetry = (error, config) =>
  Boolean(error?.response) &&
  error.response.status === 401 &&
  !config?.[RETRIED] &&
  isSameOriginWrite(config)

const retryWithFreshToken = async (instance, error, config) => {
  const { token, authenticated } = await refreshCsrfToken()

  if (authenticated !== true || !token || token === sentToken(config)) {
    return Promise.reject(error)
  }

  return instance.request({
    ...config,
    [RETRIED]: true,
    headers: { ...config.headers, [CSRF_HEADER]: token },
  })
}

// Per-instance, so a second call cannot stack a second interceptor and set the
// header twice. A WeakSet keeps no strong reference to a discarded instance.
const installedOn = new WeakSet()

export const installCsrfInterceptor = (instance = axios) => {
  if (installedOn.has(instance)) {
    return instance
  }
  installedOn.add(instance)

  instance.interceptors.request.use(
    (config) => {
      if (!WRITE_METHODS.includes((config.method || '').toLowerCase())) {
        return config
      }

      if (!isSameOriginRequest(config.url, config.baseURL)) {
        return config
      }

      const token = getCsrfToken()
      if (token) {
        config.headers = { ...config.headers, [CSRF_HEADER]: token }
      }

      return config
    },
    (error) => Promise.reject(error)
  )

  // Capture, so the tab heals after `rotate_session` swaps the raw token out from
  // under the mask the page was rendered with. See `csrfToken.js` for why the meta
  // tag alone cannot survive that.
  //
  // ON EVERY RESPONSE, not just writes: a GET is usually what a poisoned tab issues
  // next, and healing on it means the following write already carries the new token.
  //
  // BOTH ARMS. The failing-write case IS the error arm — a tab whose recent traffic
  // is all 4xx is precisely the tab that needs to heal, so a success-only capture
  // fixes every tab except the broken one.
  //
  // Gated on origin like the attach, in the opposite direction: there it stops our
  // token leaking to a third party, here it stops a third-party response writing
  // OUR holder. Both fail closed on an unparseable URL.
  const capture = (config, headers) => {
    if (config && isSameOriginRequest(config.url, config.baseURL)) {
      captureCsrfToken(headers)
    }
  }

  instance.interceptors.response.use(
    (response) => {
      capture(response?.config, response?.headers)
      return response
    },
    (error) => {
      // `error.response` is absent on a network failure or a cancellation;
      // `error.config` still names the request, and there are no headers to read.
      const config = error?.config ?? error?.response?.config
      capture(config, error?.response?.headers)

      if (shouldRetry(error, config)) {
        return retryWithFreshToken(instance, error, config)
      }

      return Promise.reject(error)
    }
  )

  return instance
}

export default installCsrfInterceptor
